import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRole, PaymentMethod, UserType, MovementType, ServiceCategory } from './types/index';
import { dividaPendenteMovimento, movimentoTemPendente } from './shared/logica/pagamentos';
import { MOCK_USERS, MOCK_COMPANIES } from './constants/index';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { normalizePhone } from './utils/phoneUtils';
import { prepareForFirestore, prepareForUpdate } from './utils/dataUtils';
import {
  movementsService,
  clientsService,
  techJobsService,
  tasksService,
  budgetsService,
  cashLogsService,
  usersService,
  acPanelRecordsService
} from './services/dataService';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Sidebar from './layouts/Sidebar';
import Header from './layouts/Header';
import PaginaLogin from './paginas/auth/PaginaLogin';
import PaginaSelecionarEmpresa from './paginas/auth/PaginaSelecionarEmpresa';
import PaginaDashboard from './paginas/dashboard/PaginaDashboard';
import SimpleDashboardView from './pages/SimpleDashboardView';
import SimpleDebtsView from './pages/SimpleDebtsView';
import SimpleTasksView from './pages/SimpleTasksView';
import PaginaMovimentos from './paginas/movimentos/PaginaMovimentos';
import PaginaAssistencias from './paginas/assistencias/PaginaAssistencias';
import PaginaAuditoriaCaixa from './paginas/caixa/PaginaAuditoriaCaixa';
import PaginaEquipa from './paginas/equipa/PaginaEquipa';
import PaginaRelatorios from './paginas/relatorios/PaginaRelatorios';
import PaginaOperacoes from './paginas/operacoes/PaginaOperacoes';
import PaginaClientes from './paginas/clientes/PaginaClientes';
import PaginaACPanelSolar from './paginas/acpanel/PaginaACPanelSolar';
import { useToast } from './hooks/useToast';
import { ToastContainer } from './components/ErrorToast';
import ConfirmModal from './components/ConfirmModal';
import './styles/App.css';

const PaginaCurso = React.lazy(() => import('./paginas/curso/PaginaCurso'));

export default function GestaoProApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [view, setView] = useState('DASHBOARD');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop: coluna esquerda recolhida
  const [forceShowMovementModal, setForceShowMovementModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [budgetViewMode, setBudgetViewMode] = useState('CREATE'); // 'CREATE' ou 'VIEW'

  const [movements, setMovements] = useState([]);
  const [techJobs, setTechJobs] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [cashLogs, setCashLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setAllUsers] = useState(MOCK_USERS);
  const [acPanelRecords, setAcPanelRecords] = useState([]);
  
  // Estado para modal de confirmação de conclusão de assistência técnica
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    techJob: null
  });

  const { toasts, showError, showSuccess, removeToast } = useToast();

  // Carregar usuários do Firestore ao montar o componente
  useEffect(() => {
    // Primeiro, tentar carregar do Firestore
    const loadUsers = async () => {
      try {
        const firestoreUsers = await usersService.getAll();
        if (firestoreUsers.length > 0) {
          setAllUsers(firestoreUsers);
        } else {
          // Se não houver usuários no Firestore, usar MOCK_USERS e migrar para Firestore
          // (apenas na primeira vez)
          for (const user of MOCK_USERS) {
            try {
              await usersService.add(user);
            } catch (error) {
              console.error("Erro ao migrar utilizador:", error);
            }
          }
          // Recarregar após migração
          const migratedUsers = await usersService.getAll();
          setAllUsers(migratedUsers);
        }
      } catch (error) {
        console.error("Erro ao carregar utilizadores:", error);
        // Em caso de erro, usar MOCK_USERS como fallback
        setAllUsers(MOCK_USERS);
      }
    };

    loadUsers();

    // Subscrever a mudanças em tempo real
    const unsubscribeUsers = usersService.subscribe((usersData) => {
      // Garantir que empregados sem userType sejam tratados como COMPLEX
      const usersWithDefaults = usersData.map(user => {
        if (user.role === UserRole.EMPLOYEE && !user.userType) {
          return { ...user, userType: UserType.COMPLEX };
        }
        return user;
      });
      setAllUsers(usersWithDefaults);
    });

    return () => {
      unsubscribeUsers();
    };
  }, []);

  // Carregar dados do Firestore quando uma empresa é selecionada
  useEffect(() => {
    if (!currentCompany) {
      setLoading(false);
      // Limpar dados quando não há empresa selecionada
      setMovements([]);
      setTechJobs([]);
      setBudgets([]);
      setCashLogs([]);
      setTasks([]);
      setClients([]);
      setAcPanelRecords([]);
      return;
    }

    setLoading(true);
    const companyId = currentCompany.id;

    // Subscrever a mudanças em tempo real
    const unsubscribeMovements = movementsService.subscribe(companyId, (data) => {
      setMovements(data);
      setLoading(false);
    });

    const unsubscribeClients = clientsService.subscribe(companyId, (clientsData) => {
      setClients(clientsData);
      // Não criar automaticamente aqui para evitar duplicados
      // Será criado apenas quando necessário no handleAddMovement
    });

    const unsubscribeTechJobs = techJobsService.subscribe(companyId, setTechJobs);
    const unsubscribeTasks = tasksService.subscribe(companyId, setTasks);
    const unsubscribeBudgets = budgetsService.subscribe(companyId, setBudgets);
    const unsubscribeCashLogs = cashLogsService.subscribe(companyId, setCashLogs);
    const unsubscribeAcPanelRecords = acPanelRecordsService.subscribe(companyId, setAcPanelRecords);

    // Cleanup ao desmontar ou mudar empresa
    return () => {
      unsubscribeMovements();
      unsubscribeClients();
      unsubscribeTechJobs();
      unsubscribeTasks();
      unsubscribeBudgets();
      unsubscribeCashLogs();
      unsubscribeAcPanelRecords();
    };
  }, [currentCompany]);

  // Eliminar registos duplicados quando acPanelRecords são carregados (apenas uma vez por empresa)
  const duplicateCleanupDoneRef = useRef(new Set());
  
  // Controlar importação automática de orçamentos (apenas uma vez por empresa)
  const budgetImportDoneRef = useRef(new Set());
  
  useEffect(() => {
    if (!currentCompany || acPanelRecords.length === 0) return;

    // Verificar se já executou para esta empresa
    if (duplicateCleanupDoneRef.current.has(currentCompany.id)) {
      return;
    }

    const removeDuplicateRecords = async () => {
      // Verificar novamente se ainda é a empresa correta (evitar race conditions)
      if (!currentCompany || duplicateCleanupDoneRef.current.has(currentCompany.id)) {
        return;
      }
      
      try {
        // Agrupar por budgetId ou movementId
        const byBudgetId = {};
        const byMovementId = {};
        
        acPanelRecords.forEach(record => {
          if (record.budgetId) {
            if (!byBudgetId[record.budgetId]) {
              byBudgetId[record.budgetId] = [];
            }
            byBudgetId[record.budgetId].push(record);
          }
          if (record.movementId) {
            if (!byMovementId[record.movementId]) {
              byMovementId[record.movementId] = [];
            }
            byMovementId[record.movementId].push(record);
          }
        });
        
        // Eliminar duplicados (manter apenas o mais recente)
        for (const group of Object.values(byBudgetId)) {
          if (group.length > 1) {
            // Ordenar por createdAt (mais recente primeiro)
            group.sort((a, b) => {
              const dateA = new Date(a.createdAt || 0);
              const dateB = new Date(b.createdAt || 0);
              return dateB - dateA;
            });
            
            // Eliminar todos exceto o primeiro (mais recente)
            for (let i = 1; i < group.length; i++) {
              await acPanelRecordsService.delete(group[i].id);
            }
          }
        }
        
        for (const group of Object.values(byMovementId)) {
          if (group.length > 1) {
            // Ordenar por createdAt (mais recente primeiro)
            group.sort((a, b) => {
              const dateA = new Date(a.createdAt || 0);
              const dateB = new Date(b.createdAt || 0);
              return dateB - dateA;
            });
            
            // Eliminar todos exceto o primeiro (mais recente)
            for (let i = 1; i < group.length; i++) {
              await acPanelRecordsService.delete(group[i].id);
            }
          }
        }
        
        // Marcar como feito para esta empresa
        duplicateCleanupDoneRef.current.add(currentCompany.id);
      } catch (error) {
        console.error('Erro ao eliminar registos duplicados:', error);
        // Em caso de erro, remover do ref para permitir nova tentativa
        if (currentCompany) {
          duplicateCleanupDoneRef.current.delete(currentCompany.id);
        }
      }
    };

    // Executar apenas uma vez após um pequeno delay
    const timeoutId = setTimeout(removeDuplicateRecords, 3000);
    
    return () => {
      clearTimeout(timeoutId);
      // O cleanup do timeout previne a execução se o componente desmontar antes do timeout
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, acPanelRecords.length]); // Executar quando acPanelRecords mudar

  // Importar orçamentos antigos automaticamente quando budgets são carregados (apenas uma vez)
  useEffect(() => {
    if (!currentCompany || !currentUser || budgets.length === 0) return;

    // Verificar se já executou para esta empresa
    if (budgetImportDoneRef.current.has(currentCompany.id)) {
      return;
    }

    const importBudgetsAutomatically = async () => {
      // Verificar novamente para evitar race conditions
      if (budgetImportDoneRef.current.has(currentCompany.id)) {
        return;
      }
      
      // Marcar como executando antes de começar
      budgetImportDoneRef.current.add(currentCompany.id);

      try {
        // Buscar registos AC/Painel Solar atuais
        const currentRecords = await acPanelRecordsService.getByCompany(currentCompany.id);
        const existingBudgetIds = new Set(currentRecords.map(r => r.budgetId).filter(Boolean));
        
        
        const acKeywords = ['ar condicionado', 'ac', 'air conditioning', 'painel solar', 'painéis solares', 'solar panel', 'solar panels'];
        
        for (const budget of budgets) {
          // Verificar se já existe um registo AC/Painel Solar para este orçamento
          if (existingBudgetIds.has(budget.id)) {
            continue; // Já existe, pular
          }
          
          // Verificar se as notas mencionam AC/Painel Solar
          const detailsLower = (budget.details || '').toLowerCase();
          const hasACOrPanelInDetails = detailsLower && acKeywords.some(keyword => detailsLower.includes(keyword));
          
          // Verificar se algum item tem categoria AR_CONDICIONADO ou PAINEIS_SOLARES
          const hasACOrPanelInItems = Array.isArray(budget.items) && budget.items.some(item => {
            const itemCategory = item && item.category;
            return itemCategory === 'AR_CONDICIONADO' || itemCategory === 'PAINEIS_SOLARES';
          });
          
          if (hasACOrPanelInDetails || hasACOrPanelInItems) {
            try {
              // Converter items do orçamento para formato products
              const products = (budget.items || []).map(item => ({
                name: item.description || item.category || '',
                ref: item.grade || ''
              }));

              await acPanelRecordsService.add({
                companyId: currentCompany.id,
                budgetId: budget.id,
                recordType: 'BUDGET',
                client: budget.client || '',
                phone: budget.phone || '',
                locality: budget.location || '',
                date: budget.date || (budget.createdAt ? new Date(budget.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
                value: budget.total || 0,
                products: products.length > 0 ? products : [],
                notes: budget.details || '',
                photos: [],
                createdBy: budget.createdBy || currentUser.id
              });
            } catch (error) {
              console.error(`Erro ao importar orçamento ${budget.id}:`, error);
            }
          }
        }
        
      } catch (error) {
        console.error('Erro ao importar orçamentos automaticamente:', error);
      }
    };

    // Executar importação automática apenas uma vez quando a empresa e budgets são carregados
    const timeoutId = setTimeout(importBudgetsAutomatically, 2000);
    
    return () => {
      clearTimeout(timeoutId);
      // Não remover do ref no cleanup para garantir que só executa uma vez por empresa
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, currentUser?.id]); // Remover budgets.length para evitar múltiplas execuções

  // Recalcular dívida do Consumidor Final automaticamente quando movimentos mudarem
  // Nota: Usamos um debounce para evitar recálculos excessivos
  useEffect(() => {
    if (!currentCompany || movements.length === 0) return;
    
    // Usar um pequeno delay para evitar recálculos excessivos durante carregamento inicial
    // e para dar tempo ao Firestore de atualizar após operações
    const timeoutId = setTimeout(() => {
      updateConsumidorFinalDebtAutomatically();
    }, 1500);
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movements.length, currentCompany?.id]); // Usar apenas length e id para evitar loops infinitos

  // Importar movimentos (serviços) antigos automaticamente quando movements e budgets são carregados
  useEffect(() => {
    if (!currentCompany || !currentUser || movements.length === 0) return;

    const importMovementsAutomatically = async () => {
      try {
        // Buscar registos AC/Painel Solar atuais
        const currentRecords = await acPanelRecordsService.getByCompany(currentCompany.id);
        const existingMovementIds = new Set(currentRecords.map(r => r.movementId).filter(Boolean));

        for (const movement of movements) {
          // Apenas processar movimentos do tipo SERVIÇO
          if (movement.type !== MovementType.SERVICO) continue;
          
          // Verificar se já existe um registo AC/Painel Solar para este movimento
          if (existingMovementIds.has(movement.id)) {
            continue; // Já existe, pular
          }
          
          let hasACOrPanel = false;
          let relatedBudget = null;
          
          // Verificar se o serviço é Ar Condicionado ou Painel Solar
          if (movement.serviceCategory === ServiceCategory.AR_CONDICIONADO || 
              movement.serviceCategory === ServiceCategory.PAINEL_SOLAR) {
            hasACOrPanel = true;
          }
          
          // Se o movimento está ligado a um orçamento, verificar também o orçamento
          if (movement.relatedBudgetId && budgets.length > 0) {
            relatedBudget = budgets.find(b => b.id === movement.relatedBudgetId);
            if (relatedBudget) {
              // Verificar se o orçamento tem itens com categoria AR_CONDICIONADO ou PAINEIS_SOLARES
              const hasACOrPanelInBudget = Array.isArray(relatedBudget.items) && relatedBudget.items.some(item => 
                item && (item.category === 'AR_CONDICIONADO' || item.category === 'PAINEIS_SOLARES')
              );
              
              if (hasACOrPanelInBudget) {
                hasACOrPanel = true;
              }
            }
          }
          
          if (hasACOrPanel) {
            try {
              // Criar registo AC/Painel Solar (TODOS os serviços, pagos ou não)
              await acPanelRecordsService.add({
                companyId: currentCompany.id,
                movementId: movement.id,
                budgetId: movement.relatedBudgetId || null,
                recordType: 'MOVEMENT',
                client: movement.client || '',
                phone: movement.phone || '',
                locality: movement.locality || (relatedBudget?.location || ''),
                street: movement.street,
                doorNumber: movement.doorNumber,
                zipCode: movement.zipCode,
                date: movement.date,
                value: movement.value || 0,
                products: movement.products || (movement.product ? [{ name: movement.product, ref: '' }] : []),
                paymentMethod: movement.paymentMethod,
                invoiceStatus: movement.invoiceStatus,
                invoiceNumber: movement.invoiceNumber,
                notes: movement.notes,
                photos: [],
                createdBy: movement.createdBy || currentUser.id
              });
            } catch (error) {
              console.error(`Erro ao importar movimento ${movement.id}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao importar movimentos automaticamente:', error);
      }
    };

    // Executar importação automática após um pequeno delay para garantir que os dados estão carregados
    const timeoutId = setTimeout(importMovementsAutomatically, 3000);
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, movements.length, budgets.length, currentUser?.id]);

  const handleLogin = async (user) => {
    const now = format(new Date(), 'HH:mm - dd/MM');
    // Garantir que empregados sem userType sejam tratados como COMPLEX
    let userWithType = { ...user };
    if (user.role === UserRole.EMPLOYEE && !user.userType) {
      userWithType.userType = UserType.COMPLEX;
    }
    const updatedUser = { ...userWithType, lastActive: now };
    setCurrentUser(updatedUser);
    setAllUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
    
    // Persistir lastActive no Firestore se o usuário já existe lá
    // Firestore IDs são strings longas (não começam com 'u' seguido de número)
    // Mock user IDs são 'u1', 'u2', etc.
    if (user.id && !user.id.match(/^u\d+$/)) {
      try {
        await usersService.update(user.id, { lastActive: now });
      } catch (error) {
        console.error("Erro ao atualizar último acesso do utilizador:", error);
        // Não bloquear o login se houver erro
      }
    }
    
    setView('DASHBOARD');
    if (user.role !== UserRole.ADMIN && user.companies.length === 1) {
      const comp = MOCK_COMPANIES.find(c => c.id === user.companies[0]);
      if (comp) setCurrentCompany(comp);
    }
  };

  const handleLogout = async () => {
    // Atualizar lastActive antes de fazer logout
    // Firestore IDs são strings longas (não começam com 'u' seguido de número)
    // Mock user IDs são 'u1', 'u2', etc.
    if (currentUser?.id && !currentUser.id.match(/^u\d+$/)) {
      try {
        const now = format(new Date(), 'HH:mm - dd/MM');
        await usersService.update(currentUser.id, { lastActive: now });
        // Atualizar estado local também
        setAllUsers(prev => prev.map(u => u.id === currentUser.id ? { ...u, lastActive: now } : u));
      } catch (error) {
        console.error("Erro ao atualizar último acesso do utilizador no logout:", error);
        // Não bloquear o logout se houver erro
      }
    }
    
    setCurrentUser(null);
    setCurrentCompany(null);
    setView('DASHBOARD');
  };

  const handleAddMovement = async (m) => {
    try {
      // Limpar campos undefined e remover id antes de guardar
      const cleanMovement = prepareForFirestore(m);
      
      const newMovement = {
        ...cleanMovement,
        companyId: currentCompany.id,
        createdBy: m.createdBy || currentUser.id // Adicionar createdBy
      };
      
      // Calcular dívida
      let deltaDebt = 0;
      deltaDebt = dividaPendenteMovimento(newMovement);

      // LÓGICA CRÍTICA: GARANTIA DE REGISTRO DE CLIENTE
      if (newMovement.saleType === 'SIMPLE') {
        // Buscar TODOS os Consumidor Final (pode haver múltiplos por erro)
        const cfList = clients.filter(c => 
          (c.name === 'Consumidor Final' || c.specialId === '160') && 
          c.companyId === currentCompany.id
        );
        
        // Encontrar o melhor candidato válido:
        // 1. Preferir o que tem specialId='160' E tem ID válido do Firestore (não é '160')
        let cf = cfList.find(c => c.specialId === '160' && c.id && c.id !== '160');
        
        // 2. Se não encontrou, buscar qualquer um com ID válido do Firestore
        if (!cf) {
          cf = cfList.find(c => c.id && c.id !== '160');
        }
        
        // 3. Se ainda não encontrou, pegar o primeiro (pode ser inválido, mas vamos tratar)
        if (!cf && cfList.length > 0) {
          cf = cfList[0];
        }
        
        // Tratar cliente encontrado
        let clientWasUpdated = false;
        if (cf && cf.id && cf.id !== '160') {
          // Cliente válido encontrado - atualizar dívida
          const currentTotalDebt = cf.totalDebt || 0;
          
          // Eliminar todos os duplicados ANTES de atualizar (evitar race conditions)
          let totalDebtFromDuplicates = 0;
          if (cfList.length > 1) {
            for (const duplicate of cfList) {
              // Ignorar o cliente principal
              if (duplicate.id === cf.id) continue;
              
              try {
                // Se o duplicado tem dívida, acumular para transferir
                if (duplicate.totalDebt && duplicate.totalDebt > 0) {
                  totalDebtFromDuplicates += duplicate.totalDebt;
                }
                
                // Eliminar duplicado (apenas se tiver ID válido)
                if (duplicate.id && duplicate.id !== '160') {
                  await clientsService.delete(duplicate.id);
                }
              } catch (err) {
                console.warn("Erro ao eliminar duplicado de Consumidor Final:", err);
                // Continuar mesmo se falhar a eliminação de um duplicado
              }
            }
          }
          
          // Atualizar o cliente principal com a dívida total (nova + existente + dos duplicados)
          await clientsService.update(cf.id, {
            totalDebt: currentTotalDebt + deltaDebt + totalDebtFromDuplicates
          });
          clientWasUpdated = true;
        } else if (cf && cf.id === '160') {
          // Cliente encontrado mas com ID inválido ('160') - tentar eliminá-lo
          try {
            await clientsService.delete(cf.id);
          } catch (err) {
            // Ignorar se já foi eliminado ou não existe no Firestore
            console.warn("Não foi possível eliminar Consumidor Final inválido:", err);
          }
          // Continuar para criar um novo válido
        }
        
        // Se não encontrou cliente válido OU não foi atualizado, criar um novo
        if (!clientWasUpdated) {
          // Primeiro, fazer uma verificação dupla para evitar race conditions
          // Buscar novamente no estado atualizado (caso tenha mudado durante as operações anteriores)
          const freshCfList = clients.filter(c => 
            (c.name === 'Consumidor Final' || c.specialId === '160') && 
            c.companyId === currentCompany.id &&
            c.id && 
            c.id !== '160'
          );
          
          if (freshCfList.length > 0) {
            // Um cliente válido foi criado por outra operação concorrente - atualizar esse
            const freshCf = freshCfList[0];
            const existingDebt = freshCf.totalDebt || 0;
            
            // Acumular dívidas de duplicados antes de fazer uma única atualização
            let totalDebtFromDuplicates = 0;
            if (freshCfList.length > 1) {
              for (const duplicate of freshCfList.slice(1)) {
                try {
                  // Acumular dívida do duplicado
                  if (duplicate.totalDebt && duplicate.totalDebt > 0) {
                    totalDebtFromDuplicates += duplicate.totalDebt;
                  }
                  // Eliminar duplicado
                  if (duplicate.id) {
                    await clientsService.delete(duplicate.id);
                  }
                } catch (err) {
                  console.warn("Erro ao consolidar duplicado:", err);
                }
              }
            }
            
            // Fazer uma única atualização com toda a dívida acumulada
            await clientsService.update(freshCf.id, {
              totalDebt: existingDebt + deltaDebt + totalDebtFromDuplicates
            });
          } else {
            // Realmente não existe nenhum cliente válido - criar novo
            // Mas primeiro, tentar eliminar qualquer inválido que possa existir
            const invalidList = clients.filter(c => 
              (c.name === 'Consumidor Final' || c.specialId === '160') && 
              c.companyId === currentCompany.id &&
              (!c.id || c.id === '160')
            );
            
            for (const invalid of invalidList) {
              if (invalid.id) {
                try {
                  await clientsService.delete(invalid.id);
                } catch (err) {
                  // Ignorar erros de eliminação (pode não existir)
                  console.warn("Erro ao eliminar Consumidor Final inválido:", err);
                }
              }
            }
            
            // Agora criar o novo cliente
            await clientsService.add({
              companyId: currentCompany.id,
              name: 'Consumidor Final',
              totalDebt: deltaDebt || 0,
              specialId: '160'
            });
          }
        }
      } else {
        // Procura por telefone normalizado (tentar vários códigos de país para retrocompatibilidade)
        const codesToTry = ['351', '49', '33', '31', '44', '34', '39', '41', '32', '45', '46', '47', '358'];
        let existing = null;
        
        if (newMovement.phone) {
          for (const code of codesToTry) {
            const normInput = normalizePhone(newMovement.phone, code);
            if (normInput && normInput.length >= 6) {
              existing = clients.find(c => {
                return c.companyId === currentCompany.id && 
                       (normalizePhone(c.phone, code) === normInput || 
                        normalizePhone(c.phone2, code) === normInput || 
                        normalizePhone(c.phone3, code) === normInput);
              });
              if (existing) break;
            }
          }
        }
        
        if (existing) {
          // Preparar updates garantindo que não há valores undefined
          const clientUpdates = {
            name: newMovement.client || existing.name || '',
            nif: newMovement.nif || existing.nif || '',
            email: newMovement.email || existing.email || '',
            locality: newMovement.locality || existing.locality || '',
            street: newMovement.street || existing.street || '',
            doorNumber: newMovement.doorNumber || existing.doorNumber || '',
            zipCode: newMovement.zipCode || existing.zipCode || '',
            totalDebt: (existing.totalDebt || 0) + deltaDebt
          };
          // Remover campos undefined antes de atualizar
          Object.keys(clientUpdates).forEach(key => {
            if (clientUpdates[key] === undefined) {
              delete clientUpdates[key];
            }
          });
          await clientsService.update(existing.id, clientUpdates);
        } else {
          // Criar cliente - o telefone já vem formatado com código do país
          await clientsService.add({
            companyId: currentCompany.id,
            name: newMovement.client,
            phone: newMovement.phone, // Já vem formatado com +código
            locality: newMovement.locality,
            street: newMovement.street,
            doorNumber: newMovement.doorNumber,
            zipCode: newMovement.zipCode,
            nif: newMovement.nif,
            email: newMovement.email,
            totalDebt: deltaDebt
          });
        }
      }

      // Adicionar movimento ao Firestore
      const movementId = await movementsService.add(newMovement);
      
      // Recalcular dívida automaticamente se for movimento SIMPLE
      // Usar setTimeout para garantir que o movimento foi adicionado ao Firestore
      if (newMovement.saleType === 'SIMPLE') {
        setTimeout(() => {
          updateConsumidorFinalDebtAutomatically();
        }, 500);
      }
      
      showSuccess('Movimento registado com sucesso');

      // Se o movimento estiver vinculado a uma assistência técnica ou orçamento e foi pago, atualizar o status
      if (newMovement.relatedTechJobId || newMovement.relatedBudgetId) {
        // Verificar se o movimento foi pago (não tem pagamento pendente)
        const isPaid = !movimentoTemPendente(newMovement);

        if (isPaid) {
          // Atualizar assistência técnica se vinculada
          if (newMovement.relatedTechJobId) {
            try {
              const techJob = techJobs.find(tj => tj.id === newMovement.relatedTechJobId);
              if (techJob && !techJob.isPaid) {
                await techJobsService.update(newMovement.relatedTechJobId, {
                  isPaid: true
                });
              }
            } catch (error) {
              console.error("Erro ao atualizar status de pagamento da assistência técnica:", error);
            }
          }

          // Atualizar orçamento se vinculado
          if (newMovement.relatedBudgetId) {
            try {
              const budget = budgets.find(b => b.id === newMovement.relatedBudgetId);
              if (budget && budget.status !== 'APROVADO') {
                // Não alterar automaticamente o status do orçamento, apenas marcar como relacionado a pagamento
                // O utilizador pode querer manter o orçamento como PENDENTE mesmo após pagamento
              }
            } catch (error) {
              console.error("Erro ao atualizar orçamento:", error);
            }
          }
        }
      }

      // Se o movimento for do tipo SERVIÇO e estiver vinculado a uma assistência técnica, mostrar modal para perguntar se foi concluída
      if (newMovement.type === MovementType.SERVICO && newMovement.relatedTechJobId) {
        try {
          const techJob = techJobs.find(tj => tj.id === newMovement.relatedTechJobId);
          if (techJob && techJob.status !== 'CONCLUIDA') {
            // Mostrar modal de confirmação
            setConfirmModal({
              isOpen: true,
              techJob: techJob
            });
          }
        } catch (error) {
          console.error("Erro ao verificar assistência técnica:", error);
        }
      }

      // Se o movimento for do tipo SERVIÇO, verificar se é AC/Painel Solar
      if (newMovement.type === MovementType.SERVICO) {
        try {
          let hasACOrPanel = false;
          let relatedBudget = null;
          
          // Verificar se o serviço é Ar Condicionado ou Painel Solar
          if (newMovement.serviceCategory === ServiceCategory.AR_CONDICIONADO || 
              newMovement.serviceCategory === ServiceCategory.PAINEL_SOLAR) {
            hasACOrPanel = true;
          }
          
          // Se o movimento está ligado a um orçamento, verificar também o orçamento
          if (newMovement.relatedBudgetId) {
            relatedBudget = budgets.find(b => b.id === newMovement.relatedBudgetId);
            if (relatedBudget) {
              // DELETAR o orçamento quando é convertido em serviço
              try {
                await budgetsService.delete(newMovement.relatedBudgetId);
              } catch (error) {
                console.error("Erro ao remover orçamento:", error);
              }
              
              if (relatedBudget.items && Array.isArray(relatedBudget.items)) {
                const hasACOrPanelInBudget = relatedBudget.items.some(item => 
                  item && (item.category === 'AR_CONDICIONADO' || item.category === 'PAINEIS_SOLARES')
                );
                
                if (hasACOrPanelInBudget) {
                  hasACOrPanel = true;
                }
              }
            }
          }
          
          if (hasACOrPanel) {
            // Criar registo AC/Painel Solar (TODOS os serviços, pagos ou não)
            await acPanelRecordsService.add({
              companyId: currentCompany.id,
              movementId: movementId,
              budgetId: newMovement.relatedBudgetId || null,
              recordType: 'MOVEMENT',
              client: newMovement.client,
              phone: newMovement.phone,
              locality: newMovement.locality || (relatedBudget?.location || ''),
              street: newMovement.street,
              doorNumber: newMovement.doorNumber,
              zipCode: newMovement.zipCode,
              date: newMovement.date,
              value: newMovement.value,
              products: newMovement.products || (newMovement.product ? [{ name: newMovement.product, ref: '' }] : []),
              paymentMethod: newMovement.paymentMethod,
              invoiceStatus: newMovement.invoiceStatus,
              invoiceNumber: newMovement.invoiceNumber,
              notes: newMovement.notes,
              photos: [],
              createdBy: currentUser.id
            });
            
            // Redirecionar para a aba AC/Painel Solar (o card aparecerá automaticamente)
            setView('AC_PANEL_SOLAR');
          }
        } catch (error) {
          console.error("Erro ao criar registo AC/Painel Solar:", error);
          // Não bloquear o movimento se falhar criar o registo AC/Painel Solar
        }
      }
    } catch (error) {
      console.error("Erro ao adicionar movimento:", error);
      const errorMessage = error.message || "Erro desconhecido ao adicionar movimento";
      showError(errorMessage);
    }
  };

  const handleAddTech = async (t) => {
    try {
      // Preservar fotos antes de limpar (garantir que arrays vazios não sejam perdidos)
      const photosToSave = Array.isArray(t.photos) ? t.photos : [];
      
      // Limpar campos undefined
      const cleanTech = prepareForFirestore(t);
      
      const newJob = {
        ...cleanTech,
        companyId: currentCompany.id,
        photos: photosToSave, // Sempre usar o array preservado
        additionalPhotos: [],
        budgets: [],
        status: 'ABERTA',
        isArchived: false,
        createdBy: currentUser.id // Adicionar createdBy
      };
      
      // Verificar se cliente existe
      const normInput = normalizePhone(newJob.phone);
      if (normInput) {
        const existing = clients.find(c => 
          c.companyId === currentCompany.id && 
          (normalizePhone(c.phone) === normInput || normalizePhone(c.phone2) === normInput)
        );
        if (!existing) {
          await clientsService.add({
            companyId: currentCompany.id,
            name: newJob.client,
            phone: newJob.phone,
            locality: newJob.locality,
            totalDebt: 0
          });
        }
      }

      await techJobsService.add(newJob);
      showSuccess('Assistência técnica adicionada com sucesso');
    } catch (error) {
      console.error("Erro ao adicionar assistência técnica:", error);
      const errorMessage = error.message || "Erro desconhecido ao adicionar assistência técnica";
      showError(errorMessage);
    }
  };

  // Função para recalcular dívida de um cliente específico baseado nos movimentos
  const updateClientDebtAutomatically = async (clientPhone, clientName) => {
    try {
      if (!currentCompany) return;

      // Buscar cliente pelo telefone ou nome
      const codesToTry = ['351', '49', '33', '31', '44', '34', '39', '41', '32', '45', '46', '47', '358'];
      let client = null;

      if (clientPhone) {
        for (const code of codesToTry) {
          const normInput = normalizePhone(clientPhone, code);
          if (normInput && normInput.length >= 6) {
            client = clients.find(c => {
              return c.companyId === currentCompany.id && 
                     (normalizePhone(c.phone, code) === normInput || 
                      normalizePhone(c.phone2, code) === normInput || 
                      normalizePhone(c.phone3, code) === normInput);
            });
            if (client) break;
          }
        }
      }

      // Se não encontrou por telefone, tentar por nome (mas apenas se não for Consumidor Final)
      // Use case-insensitive matching and trim whitespace
      if (!client && clientName && clientName !== 'Consumidor Final') {
        const normalizedClientName = clientName.trim().toLowerCase();
        client = clients.find(c => {
          if (c.companyId !== currentCompany.id) return false;
          const normalizedCName = (c.name || '').trim().toLowerCase();
          return normalizedCName === normalizedClientName;
        });
      }

      if (!client || !client.id) {
        console.log('Cliente não encontrado para atualizar dívida:', { clientPhone, clientName });
        return;
      }

      // Buscar todos os movimentos deste cliente
      // Use both phone and name matching to be more robust
      const clientMovements = movements.filter(m => {
        if (m.companyId !== currentCompany.id) return false;
        
        // Verificar por telefone
        if (clientPhone) {
          for (const code of codesToTry) {
            const normInput = normalizePhone(clientPhone, code);
            if (normInput && normInput.length >= 6) {
              const movementPhone = normalizePhone(m.phone, code);
              if (movementPhone === normInput) return true;
            }
          }
        }
        
        // Verificar por nome (case-insensitive, trimmed)
        if (clientName) {
          const normalizedClientName = clientName.trim().toLowerCase();
          const normalizedMovementClient = (m.client || '').trim().toLowerCase();
          if (normalizedMovementClient === normalizedClientName) return true;
        }
        
        return false;
      });

      // Calcular dívida pendente real dos movimentos (apenas PENDING payments)
      let calculatedDebt = 0;
      clientMovements.forEach(m => {
        calculatedDebt += dividaPendenteMovimento(m);
      });

      // Atualizar sempre para garantir sincronização (mesmo se o valor não mudou muito)
      // Isso garante que após uma liquidação, o totalDebt seja atualizado corretamente
      await clientsService.update(client.id, {
        totalDebt: calculatedDebt
      });
      
      console.log('Dívida do cliente atualizada:', {
        clientName: client.name,
        oldDebt: client.totalDebt,
        newDebt: calculatedDebt,
        movementsCount: clientMovements.length
      });
    } catch (error) {
      console.error("Erro ao atualizar dívida do cliente:", error);
      // Não mostrar erro ao usuário - é uma atualização silenciosa em background
    }
  };

  const handleEditMovement = async (m) => {
    try {
      // Limpar campos undefined antes de atualizar
      let { id, updates } = prepareForUpdate(m);
      // Garantir que invoiceNumber seja limpo quando status não é FATURADO
      if (updates.invoiceStatus === 'PARA_FATURAR' || updates.invoiceStatus === 'NAO_FATURADO') {
        updates = { ...updates, invoiceNumber: '' };
      }
      
      // Guardar informações do movimento antes de atualizar para recalcular dívida
      const oldMovement = movements.find(mov => mov.id === id);
      const wasSimple = oldMovement?.saleType === 'SIMPLE';
      const isSimple = updates.saleType === 'SIMPLE' || (oldMovement?.saleType === 'SIMPLE' && updates.saleType === undefined);
      
      // Merge updates with old movement to get the updated movement data
      const updatedMovement = { ...oldMovement, ...updates };
      
      await movementsService.update(id, updates);
      
      // Sempre recalcular dívida quando um movimento é atualizado
      // Isso garante que mudanças no método de pagamento (PENDING -> CARD/CASH/TRANSFER) sejam refletidas
      // Use multiple retries with increasing delays to ensure Firestore has updated and state has refreshed
      const clientName = updatedMovement.client || oldMovement?.client;
      const clientPhone = updatedMovement.phone || oldMovement?.phone;
      
      // First attempt after 1 second
      setTimeout(() => {
        if (wasSimple || isSimple) {
          updateConsumidorFinalDebtAutomatically();
        }
        if (clientName && clientName !== 'Consumidor Final') {
          updateClientDebtAutomatically(clientPhone, clientName);
        }
      }, 1000);
      
      // Retry after 3 seconds (in case first attempt was too early)
      setTimeout(() => {
        if (wasSimple || isSimple) {
          updateConsumidorFinalDebtAutomatically();
        }
        if (clientName && clientName !== 'Consumidor Final') {
          updateClientDebtAutomatically(clientPhone, clientName);
        }
      }, 3000);
      
      showSuccess('Movimento atualizado com sucesso');
    } catch (error) {
      console.error("Erro ao atualizar movimento:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar movimento";
      showError(errorMessage);
    }
  };

  const handleDeleteMovement = async (id) => {
    try {
      if (window.confirm("Tem certeza que deseja eliminar este movimento?")) {
        // Guardar informação do movimento antes de eliminar
        const movementToDelete = movements.find(m => m.id === id);
        const wasSimple = movementToDelete?.saleType === 'SIMPLE';
        const clientName = movementToDelete?.client;
        const clientPhone = movementToDelete?.phone;
        
        await movementsService.delete(id);
        
        // Recalcular dívida automaticamente se era movimento SIMPLE
        if (wasSimple) {
          setTimeout(() => {
            updateConsumidorFinalDebtAutomatically();
          }, 500);
        } else if (clientName && clientName !== 'Consumidor Final') {
          // Se for cliente específico, recalcular dívida
          setTimeout(() => {
            updateClientDebtAutomatically(clientPhone, clientName);
          }, 500);
        }
        
        showSuccess('Movimento eliminado com sucesso');
      }
    } catch (error) {
      console.error("Erro ao eliminar movimento:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar movimento";
      showError(errorMessage);
    }
  };

  const handleUpdateClient = async (c) => {
    try {
      // Limpar campos undefined antes de atualizar
      const { id, updates } = prepareForUpdate(c);
      
      await clientsService.update(id, updates);
      showSuccess('Cliente atualizado com sucesso');
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar cliente";
      showError(errorMessage);
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await clientsService.delete(id);
      showSuccess('Cliente eliminado com sucesso');
    } catch (error) {
      console.error("Erro ao eliminar cliente:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar cliente";
      showError(errorMessage);
    }
  };

  const handleAddClient = async (c) => {
    try {
      // Limpar campos undefined
      const cleanClient = prepareForFirestore(c);
      
      await clientsService.add({
        ...cleanClient,
        companyId: currentCompany.id,
        totalDebt: 0
      });
      showSuccess('Cliente adicionado com sucesso');
    } catch (error) {
      console.error("Erro ao adicionar cliente:", error);
      const errorMessage = error.message || "Erro desconhecido ao adicionar cliente";
      showError(errorMessage);
    }
  };

  // Função PRIVADA para recalcular automaticamente a dívida do Consumidor Final
  // Esta função recalcula a dívida baseada nos movimentos reais (sem mostrar mensagens ao usuário)
  const updateConsumidorFinalDebtAutomatically = async () => {
    try {
      if (!currentCompany) return;

      // Encontrar o cliente "Consumidor Final"
      const cfList = clients.filter(c => 
        (c.name === 'Consumidor Final' || c.specialId === '160') && 
        c.companyId === currentCompany.id
      );
      
      // Encontrar o melhor candidato válido:
      // 1. Preferir o que tem specialId='160' E tem ID válido do Firestore (não é '160')
      let cf = cfList.find(c => c.specialId === '160' && c.id && c.id !== '160');
      
      // 2. Se não encontrou, buscar qualquer um com ID válido do Firestore
      if (!cf) {
        cf = cfList.find(c => c.id && c.id !== '160');
      }
      
      // Se não existe cliente válido, não fazer nada (será criado quando necessário)
      if (!cf || !cf.id || cf.id === '160') {
        return;
      }

      // Calcular a dívida real baseada nos movimentos
      const simpleMovements = movements.filter(m => 
        m.saleType === 'SIMPLE' && 
        m.companyId === currentCompany.id
      );

      let calculatedDebt = 0;
      simpleMovements.forEach(m => {
        calculatedDebt += dividaPendenteMovimento(m);
      });

      // Eliminar duplicados se existirem
      if (cfList.length > 1) {
        for (const duplicate of cfList) {
          if (duplicate.id === cf.id) continue;
          
          try {
            if (duplicate.id && duplicate.id !== '160') {
              await clientsService.delete(duplicate.id);
            }
          } catch (err) {
            console.warn("Erro ao eliminar duplicado de Consumidor Final:", err);
          }
        }
      }

      // Atualizar apenas se o valor mudou (evitar atualizações desnecessárias)
      const currentDebt = cf.totalDebt || 0;
      if (Math.abs(currentDebt - calculatedDebt) > 0.01) {
        await clientsService.update(cf.id, {
          totalDebt: calculatedDebt
        });
      }
    } catch (error) {
      console.error("Erro ao atualizar dívida automaticamente:", error);
      // Não mostrar erro ao usuário - é uma atualização silenciosa em background
    }
  };

  // Função PÚBLICA para recalculcar manualmente (mantida para compatibilidade, mas não deve ser usada)
  // Esta função mostra mensagens ao usuário
  const handleRefreshConsumidorFinalDebt = async () => {
    try {
      if (!currentCompany) return;

      const cfList = clients.filter(c => 
        (c.name === 'Consumidor Final' || c.specialId === '160') && 
        c.companyId === currentCompany.id
      );
      
      let cf = cfList.find(c => c.specialId === '160' && c.id && c.id !== '160');
      if (!cf) {
        cf = cfList.find(c => c.id && c.id !== '160');
      }
      
      if (!cf || !cf.id || cf.id === '160') {
        showError('Consumidor Final válido não encontrado. Por favor, crie um movimento simples primeiro.');
        return;
      }

      const simpleMovements = movements.filter(m => 
        m.saleType === 'SIMPLE' && 
        m.companyId === currentCompany.id
      );

      let calculatedDebt = 0;
      simpleMovements.forEach(m => {
        calculatedDebt += dividaPendenteMovimento(m);
      });

      if (cfList.length > 1) {
        for (const duplicate of cfList) {
          if (duplicate.id === cf.id) continue;
          try {
            if (duplicate.id && duplicate.id !== '160') {
              await clientsService.delete(duplicate.id);
            }
          } catch (err) {
            console.warn("Erro ao eliminar duplicado de Consumidor Final:", err);
          }
        }
      }

      await clientsService.update(cf.id, {
        totalDebt: calculatedDebt
      });

      showSuccess(`Dívida do Consumidor Final atualizada: ${calculatedDebt.toFixed(2)}€`);
    } catch (error) {
      console.error("Erro ao atualizar dívida do Consumidor Final:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar dívida";
      showError(errorMessage);
    }
  };

  const handleSaveCashLog = async (log) => {
    try {
      // Limpar campos undefined
      const cleanLog = prepareForFirestore(log);
      
      await cashLogsService.add({
        ...cleanLog,
        companyId: currentCompany.id,
        closedBy: currentUser.name,
        closedAt: new Date().toISOString()
      });
      showSuccess('Auditoria de caixa guardada com sucesso');
    } catch (error) {
      console.error("Erro ao guardar auditoria de caixa:", error);
      const errorMessage = error.message || "Erro desconhecido ao guardar auditoria de caixa";
      showError(errorMessage);
    }
  };

  const handleDeleteCashLog = async (id) => {
    try {
      if (!window.confirm('Tem a certeza que deseja eliminar este registo de auditoria? Esta ação não pode ser desfeita.')) {
        return;
      }
      await cashLogsService.delete(id);
      showSuccess('Registo de auditoria eliminado com sucesso');
    } catch (error) {
      console.error("Erro ao eliminar registo de auditoria:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar registo de auditoria";
      showError(errorMessage);
    }
  };

  const handleAddBudget = async (b) => {
    try {
      // Limpar campos undefined
      const cleanBudget = prepareForFirestore(b);
      
      const budgetId = await budgetsService.add({
        ...cleanBudget,
        companyId: currentCompany.id
      });
      showSuccess('Orçamento adicionado com sucesso');

      // Verificar se as notas (details) ou items mencionam AC/Painel Solar e criar registo se aplicável
      const detailsLower = (b.details || '').toLowerCase();
      const acKeywords = ['ar condicionado', 'ac', 'air conditioning', 'painel solar', 'painéis solares', 'solar panel', 'solar panels'];
      const hasACOrPanelInDetails = detailsLower && acKeywords.some(keyword => detailsLower.includes(keyword));
      
      // Verificar também se algum item tem categoria AR_CONDICIONADO ou PAINEIS_SOLARES
      const hasACOrPanelInItems = Array.isArray(b.items) && b.items.some(item => {
        const itemCategory = item && item.category;
        return itemCategory === 'AR_CONDICIONADO' || itemCategory === 'PAINEIS_SOLARES';
      });
      
      if (hasACOrPanelInDetails || hasACOrPanelInItems) {
        try {
          // Converter items do orçamento para formato products
          const products = (b.items || []).map(item => ({
            name: item.description || item.category || '',
            ref: item.grade || ''
          }));

          await acPanelRecordsService.add({
            companyId: currentCompany.id,
            budgetId: budgetId,
            recordType: 'BUDGET', // Marcar como orçamento
            client: b.client || '',
            phone: b.phone || '',
            locality: b.location || '',
            date: b.date || new Date().toISOString().split('T')[0],
            value: b.total || 0,
            products: products.length > 0 ? products : [],
            notes: b.details || '',
            photos: [],
            createdBy: currentUser.id
          });
          
          // Redirecionar para a aba AC/Painel Solar (o card aparecerá automaticamente)
          setView('AC_PANEL_SOLAR');
        } catch (error) {
          console.error("Erro ao criar registo AC/Painel Solar a partir do orçamento:", error);
          // Não bloquear a criação do orçamento se falhar criar o registo AC/Painel Solar
        }
      }
    } catch (error) {
      console.error("Erro ao adicionar orçamento:", error);
      const errorMessage = error.message || "Erro desconhecido ao adicionar orçamento";
      showError(errorMessage);
    }
  };

  const handleUpdateBudget = async (b) => {
    try {
      // Limpar campos undefined antes de atualizar
      const { id, updates } = prepareForUpdate(b);
      
      await budgetsService.update(id, updates);
      showSuccess('Orçamento atualizado com sucesso');
    } catch (error) {
      console.error("Erro ao atualizar orçamento:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar orçamento";
      showError(errorMessage);
    }
  };

  const handleUpdateTechJob = async (updated) => {
    try {
      // Preservar arrays de fotos antes de limpar
      const photosToUpdate = Array.isArray(updated.photos) ? updated.photos : (updated.photos || []);
      const additionalPhotosToUpdate = Array.isArray(updated.additionalPhotos) ? updated.additionalPhotos : (updated.additionalPhotos || []);
      
      // Limpar campos undefined antes de atualizar
      const { id, updates } = prepareForUpdate(updated);
      
      // Garantir que os arrays de fotos sejam sempre incluídos
      const finalUpdates = {
        ...updates,
        photos: photosToUpdate,
        additionalPhotos: additionalPhotosToUpdate
      };
      
      await techJobsService.update(id, finalUpdates);
      showSuccess('Assistência técnica atualizada com sucesso');
    } catch (error) {
      console.error("Erro ao atualizar assistência técnica:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar assistência técnica";
      showError(errorMessage);
    }
  };

  const handleDeleteTechJob = async (id) => {
    try {
      await techJobsService.delete(id);
      showSuccess('Assistência técnica eliminada com sucesso');
    } catch (error) {
      console.error("Erro ao eliminar assistência técnica:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar assistência técnica";
      showError(errorMessage);
    }
  };

  const handleDeleteBudget = async (id) => {
    try {
      await budgetsService.delete(id);
      showSuccess('Orçamento eliminado com sucesso');
    } catch (error) {
      console.error("Erro ao eliminar orçamento:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar orçamento";
      showError(errorMessage);
    }
  };

  const handleUpdateACPanelRecord = async (updated) => {
    try {
      // Preservar array de fotos antes de limpar
      const photosToUpdate = Array.isArray(updated.photos) ? updated.photos : (updated.photos || []);
      
      // Limpar campos undefined antes de atualizar
      const { id, updates } = prepareForUpdate(updated);
      
      // Garantir que o array de fotos seja sempre incluído
      const finalUpdates = {
        ...updates,
        photos: photosToUpdate
      };
      
      await acPanelRecordsService.update(id, finalUpdates);
      showSuccess('Registo AC/Painel Solar atualizado com sucesso');
    } catch (error) {
      console.error("Erro ao atualizar registo AC/Painel Solar:", error);
      const errorMessage = error.message || "Erro desconhecido ao atualizar registo AC/Painel Solar";
      showError(errorMessage);
    }
  };

  const handleDeleteACPanelRecord = async (id) => {
    try {
      await acPanelRecordsService.delete(id);
      showSuccess('Registo AC/Painel Solar eliminado com sucesso');
    } catch (error) {
      console.error("Erro ao eliminar registo AC/Painel Solar:", error);
      const errorMessage = error.message || "Erro desconhecido ao eliminar registo AC/Painel Solar";
      showError(errorMessage);
    }
  };

  // Mostrar apenas registos AC/Painel Solar cuja origem (movimento/orçamento) ainda existe
  const activeACPanelRecords = useMemo(() => {
    return acPanelRecords.filter(record => {
      if (record.recordType === 'MOVEMENT' && record.movementId) {
        return movements.some(m => m.id === record.movementId);
      }
      if (record.recordType === 'BUDGET' && record.budgetId) {
        return budgets.some(b => b.id === record.budgetId);
      }
      return true;
    });
  }, [acPanelRecords, movements, budgets]);

  // Limpar automaticamente tarefas não recorrentes concluídas há mais de 1 dia
  useEffect(() => {
    if (!currentCompany || !tasks || tasks.length === 0) return;

    const now = new Date();

    const tasksToKeep = [];
    const tasksToDelete = [];

    for (const task of tasks) {
      const isRecurring = task.autoSchedule && task.repeatType;
      if (isRecurring) {
        tasksToKeep.push(task);
        continue;
      }

      const isCompleted = task.isCompleted || task.status === 'Concluído';
      if (!isCompleted) {
        tasksToKeep.push(task);
        continue;
      }

      // Usar completedDate se existir; caso contrário, cair para a data da tarefa
      let completedDateStr = null;
      if (task.completedDate) {
        if (typeof task.completedDate === 'string') {
          completedDateStr = task.completedDate.slice(0, 10);
        } else if (typeof task.completedDate?.toDate === 'function') {
          completedDateStr = task.completedDate.toDate().toISOString().slice(0, 10);
        }
      }
      if (!completedDateStr && task.date) {
        if (typeof task.date === 'string') {
          completedDateStr = task.date.slice(0, 10);
        } else if (typeof task.date?.toDate === 'function') {
          completedDateStr = task.date.toDate().toISOString().slice(0, 10);
        }
      }

      if (!completedDateStr) {
        tasksToKeep.push(task);
        continue;
      }

      let diffDays = 0;
      try {
        const completedDate = parseISO(completedDateStr);
        diffDays = differenceInCalendarDays(now, completedDate);
      } catch {
        tasksToKeep.push(task);
        continue;
      }

      if (diffDays >= 1) {
        tasksToDelete.push(task);
      } else {
        tasksToKeep.push(task);
      }
    }

    if (tasksToDelete.length === 0) return;

    // Remover do Firestore em background
    tasksToDelete.forEach(async (task) => {
      if (!task.id) return;
      try {
        await tasksService.delete(task.id);
      } catch (error) {
        console.error('Erro ao eliminar tarefa concluída automaticamente:', error);
      }
    });

    // Atualizar estado local para refletir remoção
    setTasks(tasksToKeep);
  }, [currentCompany, tasks]);

  // Wrapper para setTasks que atualiza o Firestore
  const handleUpdateTasks = (updater) => {
    if (typeof updater === 'function') {
      // Se for uma função, aplicar ao estado atual
      const newTasks = updater(tasks);
      const currentTaskIds = new Set(tasks.map(t => t.id));
      const newTaskIds = new Set(newTasks.map(t => t.id));
      
      // Processar tarefas de forma assíncrona
      Promise.all(
        newTasks.map(async (task) => {
          // Tarefas novas (sem ID ou com ID temporário que não existe no estado atual)
          if (!task.id || (!currentTaskIds.has(task.id) && task.id.startsWith('task-'))) {
            // Nova tarefa - adicionar ao Firestore
            try {
              const taskData = { ...task };
              delete taskData.id; // Remover ID temporário
              const newId = await tasksService.add({
                ...taskData,
                companyId: currentCompany.id
              });
              // Atualizar o ID no estado local
              task.id = newId;
            } catch (error) {
              console.error("Erro ao adicionar tarefa:", error);
            }
          } else if (currentTaskIds.has(task.id)) {
            // Tarefa existente - verificar se mudou
            const oldTask = tasks.find(t => t.id === task.id);
            if (oldTask) {
              // Comparar campos relevantes (ignorar timestamps)
              const oldTaskClean = { ...oldTask };
              const newTaskClean = { ...task };
              delete oldTaskClean.createdAt;
              delete oldTaskClean.updatedAt;
              delete newTaskClean.createdAt;
              delete newTaskClean.updatedAt;
              
              if (JSON.stringify(oldTaskClean) !== JSON.stringify(newTaskClean)) {
                // Atualizar no Firestore
                try {
                  await tasksService.update(task.id, task);
                } catch (error) {
                  console.error("Erro ao atualizar tarefa:", error);
                }
              }
            }
          }
        })
      ).then(() => {
        // Tarefas eliminadas
        tasks.forEach(async (task) => {
          if (!newTaskIds.has(task.id) && task.id && !task.id.startsWith('task-')) {
            try {
              await tasksService.delete(task.id);
            } catch (error) {
              console.error("Erro ao eliminar tarefa:", error);
            }
          }
        });
      });
      
      setTasks(newTasks);
    } else {
      // Se for um array direto
      setTasks(updater);
    }
  };

  const isOwnerOrAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;

  // UX mobile: bloquear scroll quando o menu lateral está aberto
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileMenuOpen]);

  // Sem sessão: garantir que o URL é sempre /gestaopro (ecrã de login)
  useEffect(() => {
    if (!currentUser) {
      const p = location.pathname;
      if (p !== '/gestaopro' && p !== '/gestaopro/') {
        navigate('/gestaopro', { replace: true });
      }
    }
  }, [currentUser, location.pathname, navigate]);

  // Curso é só para empregados: se a vista for CURSO e o utilizador não for EMPLOYEE, voltar ao Dashboard
  useEffect(() => {
    if (currentUser && currentCompany && view === 'CURSO' && currentUser.role !== UserRole.EMPLOYEE) {
      setView('DASHBOARD');
    }
  }, [currentUser, currentCompany, view]);

  if (!currentUser) return <PaginaLogin onLogin={handleLogin} users={users} />;

  // Empregados não podem usar o sistema entre as 22:00 e as 07:00
  const isEmployeeAccessBlocked = () => {
    const hour = new Date().getHours();
    return hour >= 22 || hour < 7;
  };
  if (currentUser.role === UserRole.EMPLOYEE && isEmployeeAccessBlocked()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1f8e9] p-6">
        <div className="bg-white p-10 rounded-[3rem] shadow-2xl max-w-md border border-green-100 text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">Acesso restrito</h2>
          <p className="text-slate-600 mb-6">O acesso ao Gestão Pro está restrito para funcionários entre as 22:00 e as 07:00. Tente novamente a partir das 07:00.</p>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full py-4 bg-slate-200 text-slate-800 font-bold rounded-2xl hover:bg-slate-300 transition-all"
          >
            Terminar sessão
          </button>
        </div>
      </div>
    );
  }

  if (!currentCompany) return <PaginaSelecionarEmpresa user={currentUser} companies={MOCK_COMPANIES} onSelect={setCurrentCompany} onLogout={handleLogout} />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1f8e9]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-800 mx-auto"></div>
          <p className="mt-4 text-green-800 font-semibold">A carregar dados...</p>
        </div>
      </div>
    );
  }

  const isSimpleUser = currentUser?.userType === UserType.SIMPLE;

  return (
    <div className="min-h-screen flex bg-[#f1f8e9] text-slate-800">
      {!isSimpleUser && (
        <>
          <Sidebar
            currentView={view}
            onViewChange={setView}
            currentUser={currentUser}
            onLogout={handleLogout}
            isMobileMenuOpen={isMobileMenuOpen}
            onCloseMobile={() => setIsMobileMenuOpen(false)}
            isCollapsed={sidebarCollapsed}
          />
          {/* Botão recolher/expandir sidebar — na linha entre sidebar e conteúdo, a meio da altura (só desktop) */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            className={`hidden lg:flex fixed top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md text-slate-500 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 z-[60] ${
              sidebarCollapsed ? 'left-[3.5rem] -translate-x-1/2' : 'left-[18rem] -translate-x-1/2'
            }`}
          >
            {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px] z-[45] lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsMobileMenuOpen(false);
              }}
              aria-label="Fechar menu"
              role="button"
              tabIndex={0}
            />
          )}
        </>
      )}
      <main className={`flex-1 ${!isSimpleUser ? (sidebarCollapsed ? 'lg:ml-14' : 'lg:ml-72') : ''} min-h-screen flex flex-col w-full overflow-x-hidden bg-[#f1f8e9] transition-[margin] duration-200`}>
        {!isSimpleUser && (
          <Header
            companyName={currentCompany.name}
            onMenuClick={() => setIsMobileMenuOpen(true)}
          />
        )}
        <div className={`flex-1 ${isSimpleUser ? 'p-0' : 'p-4 sm:p-6 lg:p-10'} bg-[#f1f8e9]`}>
          {view === 'DASHBOARD' && (
            currentUser?.userType === UserType.SIMPLE ? (
              <SimpleDashboardView
                company={currentCompany}
                movements={movements}
                budgets={budgets}
                tasks={tasks}
                techJobs={techJobs}
                onViewMovements={() => setView('MOVEMENTS')}
                onViewDebts={() => setView('DEBTS')}
                onViewBudgets={(mode) => {
                  setView('BUDGETS');
                  // Armazenar o modo (CREATE ou VIEW) no estado
                  setBudgetViewMode(mode || 'CREATE');
                }}
                onViewTasks={() => setView('TASKS')}
                onViewACPanel={() => setView('AC_PANEL_SOLAR')}
                onLogout={handleLogout}
              />
            ) : (
              <PaginaDashboard
                company={currentCompany}
                movements={movements}
                currentUser={currentUser}
                onNewMovement={() => {
                  setView('MOVEMENTS');
                  setForceShowMovementModal(true);
                }}
                onViewAll={() => setView('MOVEMENTS')}
              />
            )
          )}
          {view === 'MOVEMENTS' && (
              <PaginaMovimentos
                company={currentCompany}
                movements={movements}
                clients={clients}
                currentUser={currentUser}
                users={users.filter(u => u.companies.includes(currentCompany.id))}
                onAdd={handleAddMovement}
                onEdit={handleEditMovement}
                onDelete={handleDeleteMovement}
                forceShowModal={forceShowMovementModal}
                onCloseModal={() => setForceShowMovementModal(false)}
                onAddClient={handleAddClient}
                techJobs={techJobs}
                budgets={budgets}
                onBack={isSimpleUser ? () => setView('DASHBOARD') : undefined}
              />
          )}
          {view === 'OPERATIONS' && (
            <PaginaOperacoes 
              currentUser={currentUser} 
              currentCompany={currentCompany} 
              movements={movements} 
              tasks={tasks} 
              techJobs={techJobs} 
              setTasks={handleUpdateTasks} 
              team={users.filter(u => u.companies.includes(currentCompany.id))}
              onBack={isSimpleUser ? () => setView('DASHBOARD') : undefined}
            />
          )}
          {view === 'TECH' && (
            <PaginaAssistencias
              company={currentCompany}
              currentUser={currentUser}
              team={users.filter(u => u.companies.includes(currentCompany.id))}
              setTasks={handleUpdateTasks}
              techJobs={techJobs}
              budgets={budgets}
              onAdd={handleAddTech}
              onUpdate={handleUpdateTechJob}
              onDelete={handleDeleteTechJob}
              onAddBudget={handleAddBudget}
              onUpdateBudget={handleUpdateBudget}
              onDeleteBudget={handleDeleteBudget}
              clients={clients}
              tasks={tasks}
            />
          )}
          {view === 'CLIENTS' && (
            <PaginaClientes 
              company={currentCompany} 
              clients={clients} 
              movements={movements} 
              techJobs={techJobs} 
              onUpdateClient={handleUpdateClient} 
              onAddClient={handleAddClient}
              onDeleteClient={handleDeleteClient}
              onRefreshConsumidorFinalDebt={handleRefreshConsumidorFinalDebt}
              currentUser={currentUser}
            />
          )}
          {view === 'CASH' && isOwnerOrAdmin && (
            <PaginaAuditoriaCaixa 
              company={currentCompany} 
              movements={movements} 
              logs={cashLogs} 
              onSave={handleSaveCashLog}
              onDelete={handleDeleteCashLog}
              currentUser={currentUser}
            />
          )}
          {view === 'USERS' && isOwnerOrAdmin && <PaginaEquipa company={currentCompany} currentUser={currentUser} users={users} setAllUsers={setAllUsers} />}
          {view === 'REPORTS' && <PaginaRelatorios movements={movements} currentUser={currentUser} />}
          {view === 'AC_PANEL_SOLAR' && (
            <PaginaACPanelSolar
              records={activeACPanelRecords}
              currentUser={currentUser}
              onUpdate={handleUpdateACPanelRecord}
              onDelete={handleDeleteACPanelRecord}
              onBack={isSimpleUser ? () => setView('DASHBOARD') : undefined}
            />
          )}
          {view === 'CURSO' && currentUser?.role === UserRole.EMPLOYEE && (
            <React.Suspense fallback={<div className="flex items-center justify-center p-10"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1b5e20]" /></div>}>
              <PaginaCurso />
            </React.Suspense>
          )}
          
          {/* Simple User Views */}
          {view === 'DEBTS' && currentUser?.userType === UserType.SIMPLE && (
            <SimpleDebtsView
              movements={movements}
              onBack={() => setView('DASHBOARD')}
              onLogout={handleLogout}
              onUpdateMovement={handleEditMovement}
            />
          )}
          {view === 'BUDGETS' && currentUser?.userType === UserType.SIMPLE && (
            <PaginaAssistencias
              company={currentCompany}
              currentUser={currentUser}
              team={users.filter(u => u.companies.includes(currentCompany.id))}
              setTasks={handleUpdateTasks}
              techJobs={techJobs}
              budgets={budgets}
              onAdd={handleAddTech}
              onUpdate={handleUpdateTechJob}
              onAddBudget={handleAddBudget}
              onUpdateBudget={handleUpdateBudget}
              onDeleteBudget={handleDeleteBudget}
              onDelete={handleDeleteTechJob}
              clients={clients}
              tasks={tasks}
              onBack={() => {
                setView('DASHBOARD');
                setBudgetViewMode('CREATE');
              }}
              initialMode={budgetViewMode}
            />
          )}
          {view === 'TASKS' && currentUser?.userType === UserType.SIMPLE && (
            <SimpleTasksView
              tasks={tasks}
              movements={movements}
              currentUser={currentUser}
              currentCompany={currentCompany}
              setTasks={handleUpdateTasks}
              team={users.filter(u => u.companies.includes(currentCompany.id))}
              onBack={() => setView('DASHBOARD')}
              onTaskClick={(task) => {
                setView('OPERATIONS');
              }}
              onNewTask={() => {
                setView('OPERATIONS');
              }}
              onLogout={handleLogout}
              onEditMovement={handleEditMovement}
            />
          )}
        </div>
      </main>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      
      {/* Modal de confirmação para concluir assistência técnica */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, techJob: null })}
        onConfirm={async () => {
          if (confirmModal.techJob) {
            try {
              await techJobsService.update(confirmModal.techJob.id, {
                status: 'CONCLUIDA',
                isPaid: true
              });
              showSuccess('Assistência técnica marcada como concluída e movida para o histórico');
            } catch (error) {
              console.error("Erro ao atualizar assistência técnica:", error);
              showError('Erro ao marcar assistência técnica como concluída');
            }
          }
          setConfirmModal({ isOpen: false, techJob: null });
        }}
        onCancel={() => setConfirmModal({ isOpen: false, techJob: null })}
        title="Assistência Técnica Concluída?"
        message={
          confirmModal.techJob
            ? `Esta assistência técnica foi concluída?\n\n` +
              `Cliente: ${confirmModal.techJob.client || '—'}\n` +
              `Avaria: ${confirmModal.techJob.description || '—'}`
            : ''
        }
        confirmText="Sim, Concluir"
        cancelText="Não, Manter Aberta"
        confirmColor="bg-green-600 hover:bg-green-700"
      />
    </div>
  );
}
